// ✅ Checks whether a newer frontend build has been deployed since this tab loaded,
// and prompts the user to refresh. Runs in production only (gated in src/index.js).
// ////////////// VersionChecker.js

import React, { useEffect, useRef } from "react";
import { notification } from "antd";
import { isNewerVersion } from "./compareVersions";

// Stable key so repeated prompts UPDATE the same toast instead of stacking a new
// one every poll (DAS-2560: the old code called notification.info with no key, so
// a fresh popup piled up every 60s).
const NOTIFICATION_KEY = "app-version-update";
const POLL_INTERVAL_MS = 60000; // check every 60 seconds

// Fetch the version string from the deployed /version.json (cache-busted).
const fetchDeployedVersion = async () => {
  try {
    const response = await fetch(`/version.json?timestamp=${Date.now()}`, {
      cache: "no-store",
    });
    const data = await response.json();
    return data?.version ?? null;
  } catch (error) {
    // Network/parse hiccup — skip this round rather than prompting on bad data.
    return null;
  }
};

const VersionChecker = () => {
  // The version this tab actually loaded with. Captured once, never overwritten,
  // so the prompt clears after a refresh (the reloaded tab anchors to the new
  // version). This is what makes a server-side divergence unable to loop forever.
  const loadedVersionRef = useRef(null);
  const promptedForRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const maybePrompt = async () => {
      const deployedVersion = await fetchDeployedVersion();
      if (cancelled || !deployedVersion) return;

      // First successful read anchors the version this tab is running.
      if (loadedVersionRef.current === null) {
        loadedVersionRef.current = deployedVersion;
        return;
      }

      // Only prompt when a strictly newer build is live than the one we loaded,
      // and only once per newer version (the stable key keeps it a single toast).
      if (
        isNewerVersion(deployedVersion, loadedVersionRef.current) &&
        promptedForRef.current !== deployedVersion
      ) {
        promptedForRef.current = deployedVersion;
        notification.info({
          key: NOTIFICATION_KEY,
          message: "New Version Available",
          description: `Version ${deployedVersion} is available. Please refresh the page.`,
          duration: 0,
          btn: (
            <button onClick={() => window.location.reload()}>Refresh Now</button>
          ),
        });
      }
    };

    maybePrompt();
    const interval = setInterval(maybePrompt, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      notification.destroy(NOTIFICATION_KEY);
    };
  }, []);

  return null;
};

export default VersionChecker;
